import config from '../../config';
import API from '../api';

const { baseUrl } = config.coreApi;

//#region Types
/**
 * @typedef Asset
 * @type {Object}
 * @property {string[]} attachedDeviceIds - Device IDs currently associated with the Asset.
 * @property {{id: string, displayName: string}[]} attachedDevices - Device details associated with the Asset.
 * @property {number} cmsMappingCount - Number of CMS mappings associated with the Asset.
 * @property {{credentialId: string, playerId: string, vendor: string}[]} cmsMappings - CMS mappings associated with the Asset.
 * @property {string} createTime - Creation timestamp.
 * @property {number} deviceCount - Number of Devices associated with the Asset.
 * @property {string} displayName - Human-readable Asset name.
 * @property {string} id - Asset ID.
 * @property {string} name - Canonical Asset resource name.
 * @property {string} siteId - ID of the Site that owns the Asset.
 * @property {"ASSET_TYPE_UNSPECIFIED" | "SCREEN" | "POSTER" | "UNKNOWN"} type - Asset type.
 * @property {string} updateTime - Last update timestamp.
 */
//#endregion

//#region CREATE
/**
 * Creates an Asset in a Site.
 * @param {string} pid - Project ID.
 * @param {string} sid - Site ID.
 * @param {Asset} asset - Asset to create.
 * @returns {Asset} Created Asset.
 */
const createAsset = async (pid, sid, asset) => {
  await API.refreshTokenWhenNeeded();
  const url = `${baseUrl}/projects/${pid}/sites/${sid}/assets`;

  return fetch(url, {
    method: 'POST',
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    body: JSON.stringify(asset)
  }).then(res => API.generateResponse(res));
};
//#endregion

//#region READ
/**
 * Lists Assets in a Project.
 * @param {string} pid - Project ID.
 * @param {string} page - Page token; omit for the first page.
 * @param {string} filter - Filter expression.
 * @param {string} orderBy - Order expression.
 * @param {number} pageSize - Maximum number of Assets to return.
 * @returns {{assets: Asset[], nextPageToken: string, totalSize: number}} Assets page.
 */
const getAssets = async (pid, page = '', filter = '', orderBy = 'displayName, id', pageSize = 40) => {
  await API.refreshTokenWhenNeeded();
  const url = `${baseUrl}/projects/${pid}/assets?pageSize=${pageSize}&pageToken=${page}&filter=${filter}&orderBy=${orderBy}`;

  return fetch(url, {
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    method: 'GET'
  }).then(res => API.generateResponse(res));
};

/**
 * Lists every Asset in a Project, following page tokens until all pages are loaded.
 * @param {string} pid - Project ID.
 * @param {string} filter - Filter expression.
 * @param {string} orderBy - Order expression.
 * @param {number} pageSize - Maximum number of Assets to request per page.
 * @returns {{assets: Asset[], nextPageToken: string, totalSize: number}} Complete Assets list.
 */
const getAllAssets = async (pid, filter = '', orderBy = 'displayName, id', pageSize = 200) => {
  const assets = [];
  const visitedPageTokens = new Set();
  let pageToken = '';
  let totalSize = 0;

  do {
    if (visitedPageTokens.has(pageToken)) {
      throw new Error('The Assets API returned a repeated page token.');
    }
    visitedPageTokens.add(pageToken);

    const response = await getAssets(pid, pageToken, filter, orderBy, pageSize);
    assets.push(...(response.assets || []));
    totalSize = response.totalSize ?? totalSize;
    pageToken = response.nextPageToken || '';
  } while (pageToken);

  return { assets, nextPageToken: '', totalSize: totalSize || assets.length };
};

/**
 * Lists Assets in a Site.
 * @param {string} pid - Project ID.
 * @param {string} sid - Site ID.
 * @param {string} page - Page token; omit for the first page.
 * @param {string} filter - Filter expression.
 * @param {string} orderBy - Order expression.
 * @param {number} pageSize - Maximum number of Assets to return.
 * @returns {{assets: Asset[], nextPageToken: string, totalSize: number}} Assets page.
 */
const getSiteAssets = async (pid, sid, page = '', filter = '', orderBy = 'displayName, id', pageSize = 40) => {
  await API.refreshTokenWhenNeeded();
  const url = `${baseUrl}/projects/${pid}/sites/${sid}/assets?pageSize=${pageSize}&pageToken=${page}&filter=${filter}&orderBy=${orderBy}`;

  return fetch(url, {
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    method: 'GET'
  }).then(res => API.generateResponse(res));
};

/**
 * Gets an Asset.
 * @param {string} pid - Project ID.
 * @param {string} sid - Site ID.
 * @param {string} aid - Asset ID.
 * @returns {Asset} Asset.
 */
const getAsset = async (pid, sid, aid) => {
  await API.refreshTokenWhenNeeded();
  const url = `${baseUrl}/projects/${pid}/sites/${sid}/assets/${aid}`;

  return fetch(url, {
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    method: 'GET'
  }).then(res => API.generateResponse(res));
};

/**
 * Lists Device choices for an Asset, including Devices attached to other Sites.
 * @param {string} pid - Project ID.
 * @param {string} sid - Site ID.
 * @param {string} aid - Asset ID.
 * @param {string} page - Page token; omit for the first page.
 * @param {string[]} deviceIds - Optional partial Device IDs.
 * @param {string} displayNameFilter - Optional partial Device display name.
 * @param {number} pageSize - Maximum number of choices to return.
 * @returns {{deviceChoices: Object[], nextPageToken: string, totalSize: number}} Device choices page.
 */
const getAssetDeviceChoices = async (
  pid,
  sid,
  aid,
  page = '',
  deviceIds = [],
  displayNameFilter = '',
  pageSize = 40
) => {
  await API.refreshTokenWhenNeeded();
  const query = new URLSearchParams({ pageSize: String(pageSize) });
  if (page) query.set('pageToken', page);
  if (deviceIds.length > 0) query.set('deviceIds', deviceIds.join(','));
  if (displayNameFilter) query.set('displayNameFilter', displayNameFilter);
  const url = `${baseUrl}/projects/${pid}/sites/${sid}/assets/${aid}/deviceChoices?${query.toString()}`;

  return fetch(url, {
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    method: 'GET'
  }).then(res => API.generateResponse(res));
};

/**
 * Lists every Device choice for an Asset, following page tokens until completion.
 * @param {string} pid - Project ID.
 * @param {string} sid - Site ID.
 * @param {string} aid - Asset ID.
 * @param {string[]} deviceIds - Optional partial Device IDs.
 * @param {string} displayNameFilter - Optional partial Device display name.
 * @param {number} pageSize - Maximum number of choices to request per page.
 * @returns {{deviceChoices: Object[], nextPageToken: string, totalSize: number}} Complete choices list.
 */
const getAllAssetDeviceChoices = async (
  pid,
  sid,
  aid,
  deviceIds = [],
  displayNameFilter = '',
  pageSize = 200
) => {
  const deviceChoices = [];
  const visitedPageTokens = new Set();
  let pageToken = '';
  let totalSize = 0;

  do {
    if (visitedPageTokens.has(pageToken)) {
      throw new Error('The Asset Device Choices API returned a repeated page token.');
    }
    visitedPageTokens.add(pageToken);

    const response = await getAssetDeviceChoices(
      pid,
      sid,
      aid,
      pageToken,
      deviceIds,
      displayNameFilter,
      pageSize
    );
    deviceChoices.push(...(response.deviceChoices || []));
    totalSize = response.totalSize ?? totalSize;
    pageToken = response.nextPageToken || '';
  } while (pageToken);

  return { deviceChoices, nextPageToken: '', totalSize: totalSize || deviceChoices.length };
};
//#endregion

//#region UPDATE
/**
 * Updates selected Asset fields.
 * @param {string} pid - Project ID.
 * @param {string} sid - Site ID.
 * @param {Asset} asset - Asset containing the ID and new field values.
 * @param {string[]} fields - Fields to update.
 * @returns {Asset} Updated Asset.
 */
const updateAsset = async (pid, sid, asset, fields) => {
  await API.refreshTokenWhenNeeded();
  const body = {};
  fields.forEach(field => {
    body[field] = asset[field];
  });

  const url = `${baseUrl}/projects/${pid}/sites/${sid}/assets/${asset.id}?updateMask=${fields.join(',')}`;
  return fetch(url, {
    method: 'PATCH',
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    body: JSON.stringify(body)
  }).then(res => API.generateResponse(res));
};

/**
 * Merges a source Asset into a target Asset in the same Site.
 * @param {string} pid - Project ID.
 * @param {string} sid - Site ID.
 * @param {string} aid - Source Asset ID.
 * @param {string} targetAssetName - Target Asset ID or canonical resource name.
 * @returns {Asset} Resulting Asset.
 */
const mergeAsset = async (pid, sid, aid, targetAssetName) => {
  await API.refreshTokenWhenNeeded();
  const url = `${baseUrl}/projects/${pid}/sites/${sid}/assets/${aid}:merge`;

  return fetch(url, {
    method: 'POST',
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    body: JSON.stringify({ targetAssetName })
  }).then(res => API.generateResponse(res));
};

/**
 * Replaces all Devices associated with an Asset.
 * @param {string} pid - Project ID.
 * @param {string} sid - Site ID.
 * @param {string} aid - Asset ID.
 * @param {string[]} deviceIds - Device IDs to associate with the Asset.
 * @returns {Asset} Updated Asset.
 */
const updateAssetAttachedDevices = async (pid, sid, aid, deviceIds) => {
  await API.refreshTokenWhenNeeded();
  const url = `${baseUrl}/projects/${pid}/sites/${sid}/assets/${aid}:setAttachedDevices`;

  return fetch(url, {
    method: 'PUT',
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    body: JSON.stringify({ deviceIds })
  }).then(res => API.generateResponse(res));
};

/**
 * Detaches Devices from Assets that belong to Sites other than the target Asset's Site.
 * @param {string} pid - Project ID.
 * @param {string} sid - Site ID.
 * @param {string} aid - Target Asset ID.
 * @param {string[]} deviceIds - Device IDs to detach.
 * @returns {{detachedDeviceIds: string[]}} Detached Device IDs.
 */
const detachAssetDevicesFromOtherSites = async (pid, sid, aid, deviceIds) => {
  await API.refreshTokenWhenNeeded();
  const url = `${baseUrl}/projects/${pid}/sites/${sid}/assets/${aid}:detachDevicesFromOtherSites`;

  return fetch(url, {
    method: 'POST',
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    body: JSON.stringify({ deviceIds })
  }).then(res => API.generateResponse(res));
};
//#endregion

//#region DELETE
/**
 * Deletes an Asset.
 * @param {string} pid - Project ID.
 * @param {string} sid - Site ID.
 * @param {string} aid - Asset ID.
 * @returns {{}} Empty response.
 */
const deleteAsset = async (pid, sid, aid) => {
  await API.refreshTokenWhenNeeded();
  const url = `${baseUrl}/projects/${pid}/sites/${sid}/assets/${aid}`;

  return fetch(url, {
    headers: { ...API.headers, 'X-Request-Id': crypto.randomUUID() },
    method: 'DELETE'
  }).then(res => API.generateResponse(res));
};
//#endregion

export {
  createAsset,
  getAssets,
  getAllAssets,
  getSiteAssets,
  getAsset,
  getAssetDeviceChoices,
  getAllAssetDeviceChoices,
  updateAsset,
  mergeAsset,
  updateAssetAttachedDevices,
  detachAssetDevicesFromOtherSites,
  deleteAsset
};
